import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { useSyncExternalStore } from "react";
import { isTauri } from "@/lib/is-tauri";

declare global {
  interface Window {
    __DENKER_OWNER__?: boolean;
    __DENKER_DISPLAY_ID__?: number;
  }
}

export type WindowRole = "owner" | "mirror";

// Seeded synchronously from the initialization_script so first paint never
// has an uncertain role. Migration is handled by the `denker://owner-changed`
// subscription below. In a non-Tauri context (e.g. the OAuth popup served
// from the web frontend), default to "owner" — there is no mirror window.
let cachedRole: WindowRole = !isTauri()
  ? "owner"
  : window.__DENKER_OWNER__ === true
    ? "owner"
    : "mirror";

const subscribers = new Set<() => void>();

// Resolved once at module load — avoids an await-interleave race where two
// rapid `owner-changed` events could resolve `getCurrentWindow()` out of order.
// Skip entirely outside Tauri: getCurrentWindow() returns undefined in a
// regular browser and would crash the whole module at import time.
const myLabel = isTauri() ? getCurrentWindow().label : null;

// Wire the reactive subscription at module load so any component using
// `useWindowRole()` sees the promotion immediately, even if it mounted
// before the event arrived.
function applyOwnerLabel(label: string | null): void {
  const next: WindowRole = label === myLabel ? "owner" : "mirror";
  if (next !== cachedRole) {
    cachedRole = next;
    subscribers.forEach((cb) => cb());
  }
}

// The init-script stamp is read before the window is built, so an owner
// change emitted before this listener registers would be lost. Once it is
// registered, read the current owner once. An event that arrives first is at
// least as new as that read, so it wins.
if (isTauri()) {
  let sawEvent = false;
  void listen<{ label: string | null; epoch: number }>(
    "denker://owner-changed",
    (e) => {
      sawEvent = true;
      applyOwnerLabel(e.payload.label);
    },
  )
    .then(() => invoke<string | null>("get_owner_label"))
    .then(
      (label) => {
        if (!sawEvent) applyOwnerLabel(label);
      },
      // Without the command the init-script stamp stays the role.
      () => {},
    );
}

/** Synchronous role read. Safe to call outside React. */
export function getWindowRoleSync(): WindowRole {
  return cachedRole;
}

/**
 * Reactive role hook. Re-renders the component when this window is
 * promoted from mirror to owner or demoted (rare) in the reverse direction.
 */
export function useWindowRole(): WindowRole {
  return useSyncExternalStore(
    (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    () => cachedRole,
  );
}

/** The CGDisplay id stamped by the Tauri initialization_script, or null. */
export function getWindowDisplayId(): number | null {
  const v = window.__DENKER_DISPLAY_ID__;
  return typeof v === "number" ? v : null;
}
